import { FileImage, FileText, Loader2, Paperclip, Star, Trash2 } from "lucide-react";
import { useCallback, useState } from "react";

import { cn } from "@/lib/utils";
import { isPdfUrl } from "@/modules/dashboard/accountsWallets/utils/attachmentUrlHelpers";
import { UserAvatar } from "@/shared/components/UserAvatar";
import type { NewsTranslations } from "@/shared/lang/interfaces/news";
import useTranslations from "@/shared/lang/useTranslations";
import { formatTimeAgo } from "@/shared/utils/dateTimeHelpers";
import { downloadAttachmentFromUrl } from "@/shared/utils/downloadAttachment";
import { colocaToast } from "@/shared/utils/toast";

import { NOTE_CATEGORY_BADGE_CLASS } from "../constants/news.constants";
import type { NewsNote } from "../types/news.types";

type NewsCardProps = {
  note: NewsNote;
  canDelete?: boolean;
  onDelete?: (note: NewsNote) => void;
};

export const NewsCard = ({ note, canDelete, onDelete }: NewsCardProps) => {
  const { t, activeLanguage } = useTranslations("news");
  const [downloadingIndex, setDownloadingIndex] = useState<number | null>(null);

  const badgeClass =
    NOTE_CATEGORY_BADGE_CLASS[note.category] ??
    "bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300";
  const label = (t(`category_${note.category}` as keyof NewsTranslations) as string) ?? note.category;

  const handleDownload = useCallback(
    async (url: string, index: number) => {
      if (downloadingIndex !== null) return;
      setDownloadingIndex(index);
      try {
        await downloadAttachmentFromUrl(url, index, "nota-archivo");
      } catch {
        colocaToast.error(t("card_attachment_download_error"));
      } finally {
        setDownloadingIndex(null);
      }
    },
    [downloadingIndex, t],
  );

  const hasAttachments = note.attachmentUrls.length > 0;

  return (
    <article
      className={cn(
        "bg-white dark:bg-zinc-800 p-6 rounded-2xl shadow-sm border border-slate-100 dark:border-zinc-700 flex flex-col",
        "transition-transform duration-200 ease-out hover:-translate-y-1",
      )}
    >
      <div className="flex justify-between items-start gap-2 mb-4">
        <span
          className={cn(
            "px-3 py-1 text-xs font-bold rounded-full uppercase tracking-wider shrink-0",
            badgeClass,
          )}
        >
          {label}
        </span>
        <div className="flex items-center gap-2 shrink-0">
          {canDelete && (
            <button
              type="button"
              onClick={() => onDelete?.(note)}
              className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 dark:text-slate-500 dark:hover:text-red-400 dark:hover:bg-red-500/10 transition-colors"
              aria-label={t("card_delete_aria")}
            >
              <Trash2 className="size-5" />
            </button>
          )}
          {note.is_pinned && (
            <Star className="size-5 fill-amber-400 text-amber-400" aria-label={t("card_pinned_aria")} />
          )}
        </div>
      </div>
      <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">{note.title}</h3>
      <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed mb-4 flex-1">
        {note.description}
      </p>

      {hasAttachments && (
        <section
          className="mb-4 rounded-xl border border-slate-200 dark:border-zinc-700 bg-slate-50/60 dark:bg-zinc-900/40 p-3"
          aria-label={t("card_attachments_aria")}
        >
          <header className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1.5 text-slate-500 dark:text-slate-400">
              <Paperclip className="size-3.5 shrink-0" aria-hidden />
              <span className="text-[11px] font-bold uppercase tracking-wider">
                {t("card_attachments_section_title")}
              </span>
            </div>
            <span className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 tabular-nums">
              {t("card_attachments_count", {
                count: note.attachmentUrls.length,
              })}
            </span>
          </header>
          <div className="flex flex-wrap items-center gap-2">
            {note.attachmentUrls.map((url, i) => {
              const isPdf = isPdfUrl(url);
              const isDownloading = downloadingIndex === i;
              return (
                <button
                  key={`${note.id}-attachment-${i}`}
                  type="button"
                  onClick={() => void handleDownload(url, i)}
                  disabled={downloadingIndex !== null}
                  className={cn(
                    "h-9 w-9 flex items-center justify-center rounded-lg shrink-0 transition-colors disabled:opacity-50 border",
                    isPdf
                      ? "bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 border-red-100 dark:border-red-900/40 hover:bg-red-100 dark:hover:bg-red-900/30"
                      : "bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 border-green-100 dark:border-green-900/40 hover:bg-green-100 dark:hover:bg-green-900/30",
                  )}
                  title={t("card_attachment_download_title", { n: i + 1 })}
                  aria-label={t("card_attachment_download_aria", { n: i + 1 })}
                >
                  {isDownloading ? (
                    <Loader2 className="size-[18px] animate-spin" />
                  ) : isPdf ? (
                    <FileText className="size-[18px]" aria-hidden />
                  ) : (
                    <FileImage className="size-[18px]" aria-hidden />
                  )}
                </button>
              );
            })}
          </div>
        </section>
      )}

      <div className="mt-auto flex items-center justify-between pt-4 border-t border-slate-100 dark:border-zinc-700/50">
        <div className="flex items-center gap-2 min-w-0">
          <UserAvatar name={note.authorName} size="sm" className="shrink-0 !h-6 !w-6 !text-xs" />
          <span className="text-xs text-slate-500 dark:text-slate-400 truncate">{note.authorName}</span>
        </div>
        <span className="text-[10px] text-slate-400 dark:text-slate-500 font-medium shrink-0 ml-2">
          {formatTimeAgo(note.updatedAt, { locale: activeLanguage })}
        </span>
      </div>
    </article>
  );
};
